import { Box, Checkbox, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Read-only state pill styles (mirrors ContactCard.STATE_STYLES).
export const STATE_PILL_STYLES = {
  no_state: { bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1", label: "no_state" },
  queued: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "Queued" },
  sent: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "Sent" },
  failed: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "Failed" },
  active: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "Active" },
  draft: { bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1", label: "Draft" },
  archived: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "Archived" },
  gis_ingested: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "GIS Ingested" },
  gis_not_found: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "GIS Not Found" },
  gis_found:        { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "GIS Found" },
  gis_notfound:     { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "GIS Not Found" },
  ingest_found:     { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "GIS Ingested" },
  ingest_not_found: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "GIS Not Found" },
  apn_good: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "APN Good" },
  apn_missing: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "APN Missing" },
  apn_not_found: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "APN Not Found" },
  apn_found: { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC", label: "APN Found" },
  apn_ambiguous: { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", label: "APN Ambiguous" },
  no_county: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "No County" },
  apn_malformed: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "APN Malformed" },
  malformed_data: { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5", label: "Malformed Data" },
};

const STATUS_CHIP_SX = {
  height: 18,
  px: 0.75,
  borderRadius: "999px",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "16px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  border: "1px solid rgba(0,0,0,0.12)",
  flexShrink: 0,
};

// ---- color helpers (mirrors admin/src/pages/Properties/PageProperties.jsx) ----

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== "string") return null;
  const cleaned = hex.replace("#", "").trim();
  if (cleaned.length !== 6) return null;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const darkenColor = (color) => {
  const hex = (color || "#111827").replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const d = (v) => Math.max(0, Math.floor(v * 0.7));
  return `rgb(${d(r)}, ${d(g)}, ${d(b)})`;
};

const STATUS_TO_COLOR = {
  available: "#10B981",
  "under contract": "#F59E0B",
  sold: "#EF4444",
  leased: "#3B82F6",
  "off market": "#6B7280",
  "for sale": "#10B981",
  for_sale: "#10B981",
  "for lease": "#3B82F6",
  for_lease: "#3B82F6",
};

const STATUS_COLOR_NAME_MAP = {
  green: "#10B981",
  red: "#EF4444",
  orange: "#F59E0B",
  blue: "#3B82F6",
  brown: "#92400E",
  gray: "#6B7280",
};

const getStatusColor = (status) =>
  STATUS_TO_COLOR[String(status || "").toLowerCase()] || "#9CA3AF";

const getNumberBackgroundColor = (property) => {
  const named = property?.details?.status_color;
  if (named && STATUS_COLOR_NAME_MAP[named]) return STATUS_COLOR_NAME_MAP[named];
  return getStatusColor(property?.details?.status || property?.status);
};

export const findCategory = (categories, value) => {
  if (!value || !Array.isArray(categories)) return null;
  return categories.find(
    (c) =>
      c?.id === value ||
      c?.value === value ||
      c?.name === value ||
      c?.label === value
  );
};

const getCategoryTint = (categories, value, alpha = 0.14) => {
  const c = findCategory(categories, value);
  return hexToRgba(c?.bgColor, alpha);
};

const getCategoryColor = (categories, value) => {
  const c = findCategory(categories, value);
  if (c?.textColor) return c.textColor;
  return darkenColor(c?.bgColor || "#111827");
};

// Shared rich property card. Used by the main Properties list (PropertyListPanel)
// and by the Contacts linked-properties panel (ContactPropertiesPanel). Interactive
// affordances (checkbox, status/category pickers, copy/delete) render only when the
// matching handler is supplied, so the card degrades cleanly to read-only.
const PropertyCard = ({
  property,
  number,
  categories = [],
  isSelected = false,
  isChecked = false,
  onSelect,
  onOpenDetails,
  onToggleSelect,
  onOpenStatusPicker,
  onOpenCategoryPicker,
  onCopy,
  onDelete,
  isStatusBusy = false,
  isDeleting = false,
  isCopying = false,
}) => {
  const categoryTint = getCategoryTint(categories, property.category, isSelected ? 0.38 : 0.2);
  const categoryColor = getCategoryColor(categories, property.category);
  const addr = property.identity?.address || {};
  const apn = property.identity?.apn || "";
  const street =
    [addr.streetNo, addr.streetName, addr.unit ? `Unit ${addr.unit}` : ""]
      .filter(Boolean)
      .join(" ")
      || addr.street
      || "";
  // Title hierarchy: street first (real address), then the `name` field if
  // it's not just the APN, then "Unnamed". Avoids the duplicate-APN look
  // when kidder data stores APN as `name`.
  const nameLooksLikeApn = property.name && property.name === apn;
  const titleText =
    street ||
    (property.name && !nameLooksLikeApn ? property.name : "") ||
    "Unnamed Property";
  const zipFull = [addr.zip, addr.zip4].filter(Boolean).join("-");
  const stateZip = [addr.state, zipFull].filter(Boolean).join(" ");
  const cityLine = [addr.city, stateZip].filter(Boolean).join(", ");
  const county = String(addr.county || "").trim();
  const statusChipColor = getNumberBackgroundColor(property);
  const statusChipLabel = String(property?.status || "Set Status");

  const statePillStyle = (() => {
    const rawState = String(property?.state || "no_state").toLowerCase();
    // For apn_good docs, prefer the GIS sub-state when set so the pill shows
    // actual GIS verification status.
    const gisState = String(property?.details?.gis?.state || "").toLowerCase();
    const key = rawState === "apn_good" && gisState ? gisState : rawState;
    return STATE_PILL_STYLES[key] || null;
  })();

  const showMetaRow = Boolean(
    property.category || statePillStyle || onOpenCategoryPicker || onCopy || onDelete
  );

  return (
    <Paper
      elevation={0}
      data-testid="property-card"
      data-property-id={property.id}
      onClick={() => onSelect?.(property)}
      onDoubleClick={() => onOpenDetails?.(property)}
      sx={{
        borderRadius: "10px",
        width: "100%",
        mb: 0.75,
        px: 1,
        py: 1,
        pl: 1.5,
        cursor: onSelect || onOpenDetails ? "pointer" : "default",
        border: "1px solid",
        borderColor: isSelected || isChecked ? "#EAB308" : "#E5E7EB",
        backgroundColor: categoryTint || "#FFFFFF",
        position: "relative",
        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: isSelected || isChecked
          ? "0 0 0 1px rgba(234, 179, 8, 0.7), 0 10px 24px rgba(234, 179, 8, 0.3)"
          : "0 2px 10px rgba(0, 0, 0, 0.08)",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 10,
          bottom: 10,
          width: isSelected || isChecked ? 5 : 4,
          borderRadius: 8,
          backgroundColor: categoryColor,
        },
        "&:hover": {
          borderColor: isSelected || isChecked ? "#EAB308" : "#CBD5E1",
          backgroundColor:
            getCategoryTint(categories, property.category, isSelected || isChecked ? 0.42 : 0.3) || "#F8FAFC",
          boxShadow: isSelected || isChecked
            ? "0 0 0 1px rgba(234, 179, 8, 0.8), 0 12px 28px rgba(234, 179, 8, 0.35)"
            : "0 3px 12px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", width: "100%", gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.6, minWidth: 0, mb: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0, flex: 1 }}>
              <Tooltip title={property.status || "Set status"} placement="bottom">
                <Box
                  onClick={onOpenStatusPicker ? (e) => onOpenStatusPicker(e, property) : undefined}
                  sx={{
                    bgcolor: getNumberBackgroundColor(property),
                    width: 22,
                    height: 22,
                    borderRadius: "999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: "white",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    cursor: onOpenStatusPicker ? (isStatusBusy ? "not-allowed" : "pointer") : "default",
                    opacity: isStatusBusy ? 0.55 : 1,
                    flexShrink: 0,
                  }}
                >
                  {number}
                </Box>
              </Tooltip>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 600, color: "#111827", fontSize: "0.85rem", lineHeight: 1.2, minWidth: 0 }}
              >
                {titleText}
              </Typography>
            </Box>
            <Tooltip title={property.status || "Set status"} placement="bottom">
              <Box
                data-testid="property-card-status"
                onClick={onOpenStatusPicker ? (e) => onOpenStatusPicker(e, property) : undefined}
                sx={{
                  ...STATUS_CHIP_SX,
                  backgroundColor: statusChipColor,
                  color: "#FFFFFF",
                  cursor: onOpenStatusPicker ? (isStatusBusy ? "not-allowed" : "pointer") : "default",
                  opacity: isStatusBusy ? 0.55 : 1,
                }}
              >
                {statusChipLabel}
              </Box>
            </Tooltip>
          </Box>
          {street && titleText !== street && (
            <Typography variant="body2" noWrap sx={{ color: "#475569", fontSize: "0.74rem", fontWeight: 500, mb: 0.15 }}>
              {street}
            </Typography>
          )}
          {cityLine && (
            <Typography variant="body2" noWrap sx={{ color: "#64748b", fontSize: "0.72rem", mb: 0.25 }}>
              {cityLine}
            </Typography>
          )}
          {(apn || county) && (
            <Typography variant="body2" noWrap sx={{ color: "#64748b", fontSize: "0.7rem", mb: 0.25 }}>
              {[apn ? `APN: ${apn}` : "", county || ""].filter(Boolean).join(" | ")}
            </Typography>
          )}
        </Box>
        {onToggleSelect && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              alignSelf: "stretch",
              flexShrink: 0,
              gap: 0.5,
            }}
          >
            <Checkbox
              size="small"
              checked={isChecked}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect?.(property)}
              sx={{ p: 0.25 }}
            />
          </Box>
        )}
      </Box>
      {showMetaRow && (
        <Box
          sx={{
            mt: 0.7,
            pt: 0.5,
            borderTop: "1px solid rgba(148,163,184,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
          }}
        >
          <Tooltip title="Change category" placement="right">
            <Box
              onClick={onOpenCategoryPicker ? (e) => onOpenCategoryPicker(e, property) : undefined}
              sx={{
                display: "inline-block",
                px: 0.75,
                py: 0.1,
                borderRadius: "999px",
                fontSize: "0.65rem",
                fontWeight: 700,
                color: categoryColor,
                backgroundColor:
                  getCategoryTint(categories, property.category, 0.5) || "#F1F5F9",
                cursor: onOpenCategoryPicker ? "pointer" : "default",
                lineHeight: 1.4,
                maxWidth: 180,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {findCategory(categories, property.category)?.label ||
                findCategory(categories, property.category)?.name ||
                property.category ||
                "Set Category"}
            </Box>
          </Tooltip>
          {statePillStyle && (
            <Tooltip title={statePillStyle.label}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  ml: "auto",
                  px: 0.75,
                  py: 0.1,
                  borderRadius: "999px",
                  border: `1px solid ${statePillStyle.border}`,
                  backgroundColor: statePillStyle.bg,
                  color: statePillStyle.fg,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                  cursor: "default",
                }}
              >
                {statePillStyle.label}
              </Box>
            </Tooltip>
          )}
          {onCopy && (
            <Tooltip title="Copy property" placement="bottom">
              <IconButton
                size="small"
                disabled={isCopying}
                onClick={(e) => onCopy(e, property)}
                sx={{
                  width: 20,
                  height: 20,
                  color: "#94A3B8",
                  "&:hover": { color: "#2563EB", backgroundColor: "#DBEAFE" },
                }}
              >
                <CopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <IconButton
              size="small"
              disabled={isDeleting}
              onClick={(e) => onDelete(e, property)}
              sx={{
                width: 20,
                height: 20,
                color: "#94A3B8",
                "&:hover": { color: "#DC2626", backgroundColor: "#FEE2E2" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PropertyCard;
